/**
 * SSH — SATIŞ SONRASI HİZMET — eski `after_sales.php` + `after_sales_insert.php` karşılığı.
 *
 * Mod A (statü yok)  → statü kartları
 * Mod B (?status=..) → o statüdeki SSH kayıtları + toplu statü değişimi + üretime gönderme
 *
 * DÜZELTİLEN ESKİ HATALAR
 *  · Fotoğraf yüklemesinde uzantı/MIME/boyut doğrulaması yoktu (.php yüklenip çalıştırılabiliyordu).
 *    Artık Cloud Storage'a `afterSales/{sshId}/photos/{ad}` yoluna, uzantı+tip+boyut kuralları ve
 *    `uploadedBy` meta damgasıyla yüklenir; dosya adı benzersizleştirilir.
 *  · Kayıt oluşturmadan ÖNCE bildirim gönderiliyordu; artık yazma başarılıysa sonuç raporlanır.
 *  · Statü sözlükten doğrulanır (Security Rules `statusValid('ssh', ...)`), istemciden gelen
 *    serbest string yazılamaz.
 *  · Üretime gönderilen SSH için üretim kaydı DOĞAL ANAHTARLIDIR (`ssh_{sshId}`) → aynı kayıt
 *    ikinci kez gönderilemez (eski "önce SELECT sonra INSERT" yarışı yapısal olarak biter).
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { doc, getDoc, limit as fsLimit, orderBy, serverTimestamp, updateDoc, where, type QueryConstraint } from "firebase/firestore";
import { getDownloadURL, ref as storageRef, uploadBytes } from "firebase/storage";

import { StatusBadge } from "../components/StatusBadge";
import { ProductPicker, type PickedProduct } from "../components/ProductPicker";
import { Modal } from "../components/Modal";
import { Toast } from "../components/Toast";
import { Spinner } from "../components/Spinner";
import { EmptyState } from "../components/EmptyState";
import { useCollection } from "../hooks/useCollection";
import { useStatusCounts } from "../hooks/useStatusCounts";
import { ListLimitNotice } from "../components/ListLimitNotice";
import { useSession, useCan } from "../lib/auth";
import { useDoc } from "../hooks/useDoc";
import { MoneyInput } from "../components/DecimalInput";
import { Field } from "../components/Field";
import { formatKurusInput, parseKurus } from "../lib/decimal";
import { tl } from "../lib/format";
import { callable, db, storage } from "../lib/firebase";
import { date, n, since } from "../lib/format";
import { errorText } from "../lib/errors";
import { PageHeader } from "../components";
import type { StatusDoc } from "../lib/types";

interface SshRow {
  id: string;
  orderId?: string;
  orderName?: string;
  customerName?: string;
  productId?: string;
  productName?: string;
  productTitle?: string;
  photoUrl?: string;
  photoPath?: string;
  quantity?: number;
  /** Göç betiği eski `volume` alanını taşır. */
  volume?: number;
  problemNote?: string;
  status: string;
  priority?: string;
  createdAt?: unknown;
  productionId?: string;
  /**
   * ★ TUTAR GİRİLDİ Mİ? — PARA DEĞİL, YALNIZCA BAYRAK.
   *
   * Tutarın KENDİSİ `afterSales/{id}/private/finance.costKurus` altındadır ve
   * yalnızca `costView` (boss + office) okuyabilir. Kök dokümanı üç rol de
   * okuduğu için para oraya yazılmaz — aynı ayrım `orders.cogs` sayaçlarında da
   * var. Liste bu bayrakla "tutar girilmemiş" rozetini çizer; ofis hangi kayda
   * tutar gireceğini görmek için her satırın gizli dokümanını açmak zorunda
   * kalmasın.
   */
  costEntered?: boolean;
}

const PRIORITY_LABEL: Record<string, string> = {
  low: "Düşük",
  normal: "Normal",
  urgent: "Acil",
  Normal: "Normal",
  Acil: "Acil",
};

/** Dosya adını Storage kurallarının beklediği güvenli biçime indirger. */
function safeFileName(name: string): string {
  const ext = (name.split(".").pop() ?? "jpg").toLowerCase().replace(/[^a-z0-9]/g, "");
  const stamp = Date.now().toString(36);
  return `foto-${stamp}.${ext || "jpg"}`;
}

/** Tek sayfada okunacak en fazla kayıt. Aşılırsa ekran bunu SÖYLER. */
const SSH_LIST_LIMIT = 300;

export default function AfterSales() {
  const [params] = useSearchParams();
  const statusParam = params.get("status") ?? "";
  const session = useSession();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [targetStatus, setTargetStatus] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [formOpen, setFormOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ kind: "success" | "error"; message: string } | null>(null);

  // Yeni SSH formu
  const [orderId, setOrderId] = useState("");
  /**
   * ★ ÜRÜN ARTIK SERBEST METİN DEĞİL — KATALOGDAN SEÇİLİR.
   *
   * Eskiden "Sorunlu Ürün ID" adında boş bir metin kutusu vardı ve ofis oraya
   * ekranda gördüğü SKU'yu yazıyordu. Katalog doküman kimlikleri 13–14 haneli
   * Shopify varyant kimlikleri (`46017909260477`), SKU ise `6081`. Sonuç:
   * canlıda 4 SSH kaydı hiçbir ürüne bağlanmadı.
   *
   * `PickedProduct` kimliği KATALOGDAN gelir; elle kimlik yazmak yapısal olarak
   * imkânsızdır. Ad da aynı seçimden geldiği için kimlik ile ad artık ayrışamaz.
   */
  const [product, setProduct] = useState<PickedProduct | null>(null);
  const [quantity, setQuantity] = useState("1");
  const [problemNote, setProblemNote] = useState("");
  const [priority, setPriority] = useState<"normal" | "urgent">("normal");
  const [photo, setPhoto] = useState<File | null>(null);

  const sshStatusConstraints = useMemo<QueryConstraint[]>(
    () => [where("category", "==", "ssh"), where("obsolete", "==", false), orderBy("sortOrder", "asc")],
    [],
  );
  const { data: sshStatuses, loading: statusesLoading } = useCollection<StatusDoc>(
    "statuses",
    sshStatusConstraints,
  );

  /**
   * ★ ÜRETİM STATÜ SÖZLÜĞÜ ARTIK BURADA OKUNMUYOR.
   * Üretim kaydının açılış statüsünü SUNUCU seçiyor (`ensureProduction`
   * `statuses/prod-hold` etiketini sözlükten okur). Tarayıcının ikinci bir
   * kopya seçim yapması, iki yolun farklı statü/etiket yazması demekti.
   */
  /**
   * ★ STATÜ KARTLARINDAKİ ADET ARTIK CANLI SAYILIR.
   *
   * Eskiden `counters/afterSales.byStatus` haritasından okunuyordu ve O
   * DOKÜMANI YAZAN HİÇBİR KOD YOKTU — göçten kalma, donmuş bir rakam. Kart
   * gerçek kayıt sayısından bağımsız bir sayı gösteriyor, hiçbir yerde hata
   * çıkmıyordu. (Aynı hata Stok Siparişi ekranında da vardı.)
   */
  const sshStatusIds = useMemo(() => sshStatuses.map((s) => s.id), [sshStatuses]);
  const { counts: statusCounts, loading: countsLoading } = useStatusCounts(
    "afterSales",
    "status",
    sshStatusIds,
  );

  const listConstraints = useMemo<QueryConstraint[]>(
    () =>
      statusParam
        ? [where("status", "==", statusParam), orderBy("createdAt", "desc"), fsLimit(SSH_LIST_LIMIT)]
        : [where("status", "==", "__none__")],
    [statusParam],
  );
  const { data: rows, loading, error } = useCollection<SshRow>("afterSales", listConstraints);

  useEffect(() => {
    if (!targetStatus && sshStatuses.length > 0) setTargetStatus(sshStatuses[0]!.id);
  }, [sshStatuses, targetStatus]);

  const initialSshStatus =
    sshStatuses.find((s) => s.id === "ssh-tobeprod")?.id ?? sshStatuses[0]?.id ?? "";
  const currentLabel = sshStatuses.find((s) => s.id === statusParam)?.label ?? statusParam ?? "SSH";

  function toggle(set: Set<string>, id: string): Set<string> {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  }

  /** Yeni SSH kaydı. Sipariş bağlantısı ZORUNLUDUR (eski kural: orderid != 0). */
  async function createSsh() {
    const uid = session?.user.uid;
    if (!uid) return;
    const qty = Number(quantity.replace(",", "."));

    if (!orderId.trim()) {
      setToast({ kind: "error", message: "Sipariş numarası zorunludur." });
      return;
    }
    if (!product) {
      setToast({ kind: "error", message: "Sorunlu ürün katalogdan seçilmelidir." });
      return;
    }
    if (!Number.isFinite(qty) || qty < 1) {
      setToast({ kind: "error", message: "Miktar en az 1 olmalıdır." });
      return;
    }
    if (!problemNote.trim()) {
      setToast({ kind: "error", message: "Açıklama zorunludur." });
      return;
    }
    if (!initialSshStatus) {
      setToast({ kind: "error", message: "SSH statü sözlüğü boş — kayıt açılamaz." });
      return;
    }

    setBusy(true);
    try {
      /**
       * ★ ÜRÜN KARTI DOĞRULAMASI ARTIK BAĞLAYICI — KAYDI ENGELLER.
       *
       * ── ASIL KÖK NEDEN BURASIYDI ─────────────────────────────────────────
       * Eski kod katalog okumasını `try/catch` içine alıp SONUCU YOK SAYIYORDU:
       * `p.exists()` false dönse bile akış devam ediyor ve kayıt, katalogda
       * karşılığı olmayan bir `productId` ile yazılıyordu. Yani doğrulama
       * görüntüsü vardı, etkisi yoktu — canlıdaki 4 bozuk kaydın tamamı bu
       * yoldan geçti.
       *
       * Artık ürün kartı okunamazsa ya da yoksa KAYIT AÇILMAZ. `ProductPicker`
       * kimliği katalogdan verdiği için bu dal normalde hiç çalışmaz; ürün bu
       * arada silinmişse diye son savunma olarak durur.
       *
       * ── ADI VE GÖRSELİ DONDURMA ──────────────────────────────────────────
       * `productTitle` ve `productImageUrl` kayıt anında yazılır; SSH üretime
       * gönderilince sunucu bunları üretim kaydının `itemSummary`'sine kopyalar.
       */
      let catalogTitle = product.name.trim();
      let catalogImageUrl = product.imageUrl.trim();
      try {
        const p = await getDoc(doc(db, "products", product.id));
        if (!p.exists()) {
          setToast({
            kind: "error",
            message:
              `Seçilen ürün katalogda bulunamadı (kimlik: ${product.id}). ` +
              "Ürün silinmiş olabilir — listeden yeniden seçin.",
          });
          setBusy(false);
          return;
        }
        const d = p.data() as { title?: unknown; name?: unknown; imageUrl?: unknown };
        if (!catalogTitle) catalogTitle = String(d.title ?? d.name ?? "").trim();
        if (!catalogImageUrl) catalogImageUrl = String(d.imageUrl ?? "").trim();
      } catch (e) {
        // ★ SESSİZ DEVAM YOK. Doğrulanamayan ürünle SSH açmak, ürüne hiç
        // bağlanmayan bir kayıt üretme riskidir; kullanıcı tekrar dener.
        setToast({
          kind: "error",
          message: `Ürün kartı doğrulanamadı: ${errorText(e)}. SSH kaydı açılmadı.`,
        });
        setBusy(false);
        return;
      }

      /**
       * ★ DOĞRUDAN `addDoc` KALDIRILDI — `createSsh` callable'ına devredildi.
       *
       * En pahalı fark SİPARİŞ BAĞLANTISI: burası `orderId`i kullanıcının
       * yazdığı gibi HAM yazıyordu. Ofis ekranda gördüğü sipariş NUMARASINI
       * ("77487") yazıyor, oysa `orders` koleksiyonu Shopify doküman kimliğiyle
       * anahtarlı — kayıt var olmayan bir siparişe bağlanıyordu. Callable'ın
       * `findOrder`u sırayla doküman kimliği → sipariş adı → arama belirteci
       * deniyor ve `orderName` ile `customerName` alanlarını da dolduruyor.
       *
       * Ayrıca callable `statusLabel` yazıyor ve DENETİM KAYDI düşürüyor;
       * ikisi de bu yolda hiç olmuyordu.
       *
       * Fotoğraf akışı AYNEN korunuyor: Storage yolu doküman kimliğini
       * gerektirdiği için önce kayıt açılır, kimlik döner, sonra yükleme yapılır
       * ve `photoPath`/`photoUrl` yamalanır — ikisi de `firestore.rules` §7
       * beyaz listesinde.
       */
      const { sshId } = await callable<
        {
          orderId: string;
          productId: string;
          volume: number;
          problemNote: string;
          priority: string;
        },
        { sshId: string }
      >("createSsh")({
        orderId: orderId.trim(),
        productId: product.id,
        // Sunucu şeması adı `volume`; ekranın `quantity` alanı buna karşılık gelir.
        volume: Math.round(qty),
        problemNote: problemNote.trim(),
        priority,
      });
      const created = doc(db, "afterSales", sshId);

      // Fotoğraf ayrı adımda: doküman kimliği Storage yolunun parçasıdır.
      if (photo) {
        try {
          const path = `afterSales/${sshId}/photos/${safeFileName(photo.name)}`;
          const r = storageRef(storage, path);
          await uploadBytes(r, photo, { customMetadata: { uploadedBy: uid } });
          const url = await getDownloadURL(r);
          await updateDoc(created, {
            photoPath: path,
            photoUrl: url,
            updatedAt: serverTimestamp(),
            updatedBy: uid,
          });
        } catch (e) {
          // Kayıt açıldı ama foto yüklenemedi — sessiz geçilmez.
          setToast({
            kind: "error",
            message: `SSH kaydı açıldı fakat fotoğraf yüklenemedi: ${errorText(e)}`,
          });
          setBusy(false);
          return;
        }
      }

      setToast({ kind: "success", message: `SSH kaydı açıldı (${sshId}).` });
      setFormOpen(false);
      setOrderId("");
      setProduct(null);
      setQuantity("1");
      setProblemNote("");
      setPriority("normal");
      setPhoto(null);
    } catch (e) {
      setToast({ kind: "error", message: errorText(e, "SSH kaydı açılamadı.") });
    } finally {
      setBusy(false);
    }
  }

  /**
   * Toplu statü değişimi. SSH statüsünün stok yan etkisi YOKTUR (eski davranış korunur).
   *
   * ★ DOĞRUDAN YAZMA KALDIRILDI — `setSshStatus` callable'ına devredildi.
   *
   * Burası `updateDoc` ile yalnızca `status` alanını yazıyordu ve üç şeyi
   * atlıyordu; üçü de sessizdi:
   *   1. `statusLabel` YAZILMIYORDU — kayıt yeni statüye geçiyor ama etiketi eski
   *      kalıyordu; listede statü bir şey, etiketi başka bir şey gösteriyordu.
   *   2. `resolvedAt` yazılmıyordu; kapanış tarihi hiç dolmuyordu.
   *   3. DENETİM KAYDI düşmüyordu — statü değişimi denetlenebilir olmalı.
   * Ayrıca hedef statü `opensProduction: true` işaretliyse ÜRETİM KAYDI
   * açılmıyordu. Aynı dosyadaki `sendToProduction` bunu callable ile doğru
   * yapıyor; bu yol sessizce atlıyordu — aynı kural, iki yol, biri eksik.
   *
   * `setSshStatus` zaten dizi alıp satır satır sonuç döndürüyor, bu yüzden
   * "başarısızları topla" davranışı olduğu gibi korunuyor.
   */
  async function changeStatus() {
    if (selected.size === 0 || !targetStatus) {
      setToast({ kind: "error", message: "Kayıt ve hedef statü seçilmelidir." });
      return;
    }
    setBusy(true);
    let failures: string[] = [];
    try {
      const out = await callable<
        { sshIds: string[]; status: string },
        { results: Array<{ sshId: string; ok: boolean; error?: string }> }
      >("setSshStatus")({ sshIds: [...selected], status: targetStatus });
      failures = (out.results ?? [])
        .filter((r) => !r.ok)
        .map((r) => `${r.sshId}: ${r.error ?? "bilinmeyen hata"}`);
    } catch (e) {
      failures = [errorText(e, "Statü güncellenemedi.")];
    }
    setBusy(false);
    setSelected(new Set());
    setToast(
      failures.length > 0
        ? { kind: "error", message: `Güncellenemeyen kayıtlar → ${failures.join(" · ")}` }
        : { kind: "success", message: "Statü güncellendi." },
    );
  }

  /**
   * Üretime gönder.
   *
   * ══════════════════════════════════════════════════════════════════════════
   * ★ NİÇİN CALLABLE — TARAYICI BU İŞİ TAM YAPAMAZ
   * ══════════════════════════════════════════════════════════════════════════
   * Burası eskiden doğrudan `production/ssh_{id}` dokümanını yazıyordu. Üretim
   * kaydı oluşuyordu ama İŞİN DİĞER YARISI HİÇ YAPILMIYORDU: SSH kaydına
   * `productionId` ve `sentToProductionAt` yazılmıyor, statüsü ilerlemiyordu.
   * Sonuç, `production/ssh.ts` başındaki (2) numaralı eski hatanın aynısıydı —
   * aynı kayıt hem "Üretilecek SSH" listesinde hem üretim listesinde duruyor,
   * hangisinin yapıldığı belirsiz kalıyordu.
   *
   * Tarayıcı bunu YAPAMAZ, ihmal değil: `firestore.rules` §7 istemcinin
   * `afterSales` üzerinde değiştirebileceği alanları
   * `changedOnly(['status','problemNote','priority','photoPath','quantity',
   * 'updatedAt','updatedBy'])` ile sınırlıyor — `productionId` ve
   * `sentToProductionAt` listede YOK, yani her deneme reddedilir.
   *
   * `setSshStatus` callable'ı iki tarafı TEK yerde yazar: statü sözlüğünde
   * `opensProduction: true` işaretli statüye geçişte `ensureProduction`
   * çağrılır, SSH kaydına bağ yazılır. Doküman kimliği yine doğal anahtardır
   * (`ssh_{sshId}`), ikinci çağrı `created: false` döner — mükerrer kayıt
   * imkânsız kalır.
   */
  async function sendToProduction() {
    if (selected.size === 0) {
      setToast({ kind: "error", message: "Önce kayıt seçin." });
      return;
    }
    // Üretim kaydını AÇAN statü sözlükten okunur; koda gömülmez.
    const opener = sshStatuses.find((s) => s.opensProduction === true);
    if (!opener) {
      setToast({
        kind: "error",
        message:
          "Statü sözlüğünde `opensProduction: true` işaretli bir SSH statüsü yok; " +
          "üretim kaydı açılamaz.",
      });
      return;
    }

    setBusy(true);
    try {
      const out = await callable<
        { sshIds: string[]; status: string },
        { results: Array<{ sshId: string; ok: boolean; productionId?: string; error?: string }> }
      >("setSshStatus")({ sshIds: [...selected], status: opener.id });

      const failures = (out.results ?? []).filter((r) => !r.ok);
      const sent = (out.results ?? []).length - failures.length;
      setToast(
        failures.length > 0
          ? {
              kind: "error",
              message:
                `${sent} kayıt gönderildi. Hatalar → ` +
                failures.map((r) => `SSH-${r.sshId}: ${r.error ?? "hata"}`).join(" · "),
            }
          : { kind: "success", message: `${sent} SSH kaydı üretime gönderildi.` },
      );
    } catch (e) {
      setToast({ kind: "error", message: errorText(e, "SSH kaydı üretime gönderilemedi.") });
    } finally {
      setBusy(false);
      setSelected(new Set());
    }
  }

  /* ── Mod A: statü kartları ───────────────────────────────────────────────── */
  if (!statusParam) {
    return (
      <PageHeader title="SSH — Satış Sonrası Hizmet">
        {toast ? <Toast kind={toast.kind} message={toast.message} onClose={() => setToast(null)} /> : null}
        <div className="mb-4">
          <button
            type="button"
            onClick={() => setFormOpen(true)}
            className="inline-flex min-h-[48px] items-center rounded-md bg-red-500 px-4 py-3 text-base font-semibold text-white shadow-sm hover:bg-red-700"
          >
            Yeni SSH Ekle
          </button>
        </div>

        {statusesLoading ? (
          <Spinner />
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {sshStatuses.map((s) => (
              <Link
                key={s.id}
                to={`?status=${encodeURIComponent(s.id)}`}
                className="flex min-h-[76px] items-center justify-between gap-3 rounded-lg bg-white p-4 text-base shadow-sm ring-1 ring-gray-200 hover:ring-primary-300"
              >
                <span className="font-semibold text-gray-900">{s.label}</span>
                {/* Sayılamayan statüde "—"; SIFIR yazılmaz. */}
                <span className="text-gray-500">
                  {countsLoading
                    ? "sayılıyor…"
                    : statusCounts[s.id] === undefined
                      ? "—"
                      : `${statusCounts[s.id]} kayıt`}
                </span>
              </Link>
            ))}
          </div>
        )}

        <SshForm
          open={formOpen}
          busy={busy}
          onClose={() => setFormOpen(false)}
          onSubmit={() => void createSsh()}
          values={{ orderId, product, quantity, problemNote, priority }}
          setters={{ setOrderId, setProduct, setQuantity, setProblemNote, setPriority, setPhoto }}
        />
      </PageHeader>
    );
  }

  /* ── Mod B: kayıt listesi ────────────────────────────────────────────────── */
  return (
    <PageHeader title={`SSH — ${currentLabel}`}>
      {toast ? <Toast kind={toast.kind} message={toast.message} onClose={() => setToast(null)} /> : null}

      <div className="mb-4 flex flex-wrap items-center gap-2 print:hidden">
        <Link
          to="/after-sales"
          className="inline-flex min-h-[48px] items-center rounded-md bg-white px-4 py-3 text-base font-semibold text-gray-600 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
        >
          ← Statüler
        </Link>
        <button
          type="button"
          onClick={() => setFormOpen(true)}
          className="inline-flex min-h-[48px] items-center rounded-md bg-red-500 px-4 py-3 text-base font-semibold text-white shadow-sm hover:bg-red-700"
        >
          Yeni SSH Ekle
        </button>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <span className="text-base font-medium text-gray-600">
            {selected.size > 0 ? `${selected.size} kayıt seçili` : "Kayıt seçin"}
          </span>
          <select
            value={targetStatus}
            onChange={(e) => setTargetStatus(e.target.value)}
            className="min-h-[48px] rounded-md border-0 bg-white px-4 py-3 text-base font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300"
          >
            {sshStatuses.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={busy || selected.size === 0}
            onClick={() => void changeStatus()}
            className="inline-flex min-h-[48px] items-center rounded-md bg-red-500 px-4 py-3 text-base font-semibold text-white shadow-sm hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Statü Değiş
          </button>
          <button
            type="button"
            disabled={busy || selected.size === 0}
            onClick={() => void sendToProduction()}
            className="inline-flex min-h-[48px] items-center rounded-lg bg-gray-900 px-4 py-3 text-base font-semibold text-white shadow-sm hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Üretime Gönder
          </button>
        </div>
      </div>

      {error ? (
        <div className="mb-4 rounded-lg bg-red-50 p-4 text-base text-red-700">
          Liste yüklenemedi: {error}
        </div>
      ) : null}

      {loading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <EmptyState title={`${currentLabel} statüsünde SSH kaydı yok`} />
      ) : (
        <div className="space-y-3">
          {rows.map((r) => {
            const isSelected = selected.has(r.id);
            const isOpen = expanded.has(r.id);
            const qty = r.quantity ?? r.volume ?? 0;
            return (
              <div key={r.id} className="rounded-lg bg-white shadow-lg">
                <div
                  className={`flex min-h-[88px] items-center justify-between gap-x-4 rounded-lg px-4 py-4 sm:px-6 ${
                    isSelected ? "ring-2 ring-inset ring-blue-700" : ""
                  }`}
                >
                  <label className="flex flex-1 cursor-pointer items-center gap-x-4">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => setSelected((s) => toggle(s, r.id))}
                      className="h-6 w-6 flex-none rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="min-w-0 flex-auto">
                      <span className="block text-base font-semibold text-gray-900">
                        {r.customerName || r.productName || r.productTitle || "SSH kaydı"}
                      </span>
                      <span className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                        <span className="inline-flex items-center rounded-md bg-red-100 px-2 py-1 text-xs font-medium text-red-700">
                          SSH {r.id}
                        </span>
                        {r.orderId ? (
                          <span className="inline-flex items-center rounded-md bg-green-100 px-2 py-1 text-xs font-medium text-green-700">
                            {r.orderName ?? r.orderId}
                          </span>
                        ) : null}
                        <StatusBadge technicName={r.status} />
                        {/*
                          ★ TUTARI GİRİLMEMİŞ KAYIT GÖRÜNÜR OLMAK ZORUNDA.
                          Finans sekmesi bu kayıtları SSH gideri toplamına
                          KATMAZ (0 TL saymaz); ofis hangi kayda tutar
                          gireceğini burada görür. Rozet PARA GÖSTERMEZ, yalnızca
                          "girildi mi" bilgisidir — üretim rolü de görebilir.
                        */}
                        {r.costEntered === true ? (
                          <span className="inline-flex items-center rounded-md bg-emerald-100 px-2 py-1 text-xs font-medium text-emerald-800">
                            Tutar girildi
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-md bg-amber-100 px-2 py-1 text-xs font-medium text-amber-900">
                            Tutar girilmemiş
                          </span>
                        )}
                        {r.priority === "urgent" || r.priority === "Acil" ? (
                          <span className="inline-flex items-center rounded-md bg-red-100 px-2 py-1 text-xs font-semibold text-red-700">
                            Acil
                          </span>
                        ) : null}
                        <span>{since(r.createdAt as never)}</span>
                      </span>
                    </span>
                  </label>

                  <div className="hidden flex-col items-end sm:flex">
                    <span className="text-base text-gray-900">{n(qty, " adet")}</span>
                    <span className="mt-1 text-xs text-gray-500">{date(r.createdAt as never)}</span>
                  </div>

                  <button
                    type="button"
                    onClick={() => setExpanded((s) => toggle(s, r.id))}
                    aria-label="Detayı göster"
                    className="inline-flex h-12 w-12 flex-none items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
                  >
                    {isOpen ? "▲" : "▼"}
                  </button>
                </div>

                {isOpen ? (
                  <div className="flex flex-wrap gap-4 rounded-b-lg bg-gray-100 p-4 shadow-inner">
                    <div className="w-full sm:w-1/4">
                      {r.photoUrl ? (
                        <img
                          src={r.photoUrl}
                          alt="SSH fotoğrafı"
                          loading="lazy"
                          className="h-40 w-full rounded object-cover"
                        />
                      ) : (
                        <div className="flex h-40 w-full items-center justify-center rounded bg-gray-200 text-sm text-gray-500">
                          Fotoğraf yok
                        </div>
                      )}
                    </div>
                    <div className="flex-1 space-y-2 text-base text-gray-800">
                      <p>{r.problemNote || "Açıklama girilmemiş."}</p>
                      <p className="text-sm text-gray-600">
                        Ürün: {r.productName ?? r.productTitle ?? r.productId ?? "—"} · Miktar:{" "}
                        {n(qty)} · Öncelik: {PRIORITY_LABEL[r.priority ?? "normal"] ?? "Normal"}
                      </p>
                      {r.productionId ? (
                        <Link
                          to={`/production/${encodeURIComponent(r.productionId)}`}
                          className="text-base font-medium text-blue-600 hover:text-blue-800"
                        >
                          Üretim kaydına git
                        </Link>
                      ) : null}

                      {/* SSH gider tutarı — yalnızca costView (boss + office). */}
                      <SshCostEditor sshId={r.id} />
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {/* ★ SESSİZ KESME YOK — kart ile liste ayrışırsa ekran SÖYLER. */}
      {statusParam ? (
        <ListLimitNotice
          shown={rows.length}
          total={statusCounts[statusParam]}
          limit={SSH_LIST_LIMIT}
        />
      ) : null}

      <SshForm
        open={formOpen}
        busy={busy}
        onClose={() => setFormOpen(false)}
        onSubmit={() => void createSsh()}
        values={{ orderId, product, quantity, problemNote, priority }}
        setters={{ setOrderId, setProduct, setQuantity, setProblemNote, setPriority, setPhoto }}
      />
    </PageHeader>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */

interface SshFormProps {
  open: boolean;
  busy: boolean;
  onClose: () => void;
  onSubmit: () => void;
  values: {
    orderId: string;
    /** ★ Katalogdan seçilen ürün; serbest metin kimlik alanı KALDIRILDI. */
    product: PickedProduct | null;
    quantity: string;
    problemNote: string;
    priority: "normal" | "urgent";
  };
  setters: {
    setOrderId: (v: string) => void;
    setProduct: (p: PickedProduct | null) => void;
    setQuantity: (v: string) => void;
    setProblemNote: (v: string) => void;
    setPriority: (v: "normal" | "urgent") => void;
    setPhoto: (f: File | null) => void;
  };
}

function SshForm({ open, busy, onClose, onSubmit, values, setters }: SshFormProps) {
  return (
    <Modal open={open} title="Yeni SSH Kaydı" onClose={onClose}>
      <div className="space-y-4">
        <div className="flex flex-col">
          <label htmlFor="ssh-order" className="text-base font-semibold text-gray-800">
            Sipariş No (zorunlu)
          </label>
          <input
            id="ssh-order"
            value={values.orderId}
            onChange={(e) => setters.setOrderId(e.target.value)}
            className="mt-1 min-h-[48px] rounded-md border border-gray-300 px-3 text-base"
            placeholder="Örn. 55123456"
          />
        </div>

        {/*
          ★ "Sorunlu Ürün ID" ve "Ürün Adı" serbest metin kutuları KALDIRILDI.
          İkisi ayrı ayrı yazılabildiği için kimlik ile ad birbirini tutmuyordu;
          kimlik alanına SKU yazılan 4 kayıt canlıda hiçbir ürüne bağlanmadı.
          `ProductPicker` kimliği, adı, SKU'yu ve görseli TEK seçimden verir.
        */}
        <ProductPicker
          value={values.product}
          onChange={setters.setProduct}
          label="Sorunlu Ürün (katalogdan seçin)"
          required
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col">
            <label htmlFor="ssh-qty" className="text-base font-semibold text-gray-800">
              Miktar
            </label>
            <input
              id="ssh-qty"
              inputMode="numeric"
              value={values.quantity}
              onChange={(e) => setters.setQuantity(e.target.value)}
              className="mt-1 min-h-[48px] rounded-md border border-gray-300 px-3 text-base"
            />
          </div>
          <div className="flex flex-col">
            <label htmlFor="ssh-priority" className="text-base font-semibold text-gray-800">
              Öncelik
            </label>
            <select
              id="ssh-priority"
              value={values.priority}
              onChange={(e) => setters.setPriority(e.target.value as "normal" | "urgent")}
              className="mt-1 min-h-[48px] rounded-md border border-gray-300 px-3 text-base"
            >
              <option value="normal">Normal</option>
              <option value="urgent">Acil</option>
            </select>
          </div>
        </div>

        <div className="flex flex-col">
          <label htmlFor="ssh-note" className="text-base font-semibold text-gray-800">
            Açıklama (zorunlu)
          </label>
          <textarea
            id="ssh-note"
            rows={3}
            value={values.problemNote}
            onChange={(e) => setters.setProblemNote(e.target.value)}
            className="mt-1 rounded-md border border-gray-300 p-3 text-base"
          />
        </div>

        <div className="flex flex-col">
          <label htmlFor="ssh-photo" className="text-base font-semibold text-gray-800">
            Fotoğraf (jpg, png, webp — en fazla 8 MB)
          </label>
          <input
            id="ssh-photo"
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            onChange={(e) => setters.setPhoto(e.target.files?.[0] ?? null)}
            className="mt-1 min-h-[48px] rounded-md border border-gray-300 p-2 text-base"
          />
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-[48px] items-center rounded-md bg-white px-4 py-3 text-base font-semibold text-gray-700 ring-1 ring-inset ring-gray-300"
          >
            Vazgeç
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onSubmit}
            className="inline-flex min-h-[48px] items-center rounded-lg bg-gray-900 px-5 py-3 text-base font-bold text-white shadow-sm hover:bg-gray-800 disabled:opacity-50"
          >
            {busy ? "Kaydediliyor…" : "Ekle"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * SSH GİDER TUTARI
 * ══════════════════════════════════════════════════════════════════════════
 *
 * ── NİÇİN AYRI DOKÜMANDAN OKUNUYOR ─────────────────────────────────────────
 * Tutar `afterSales/{id}/private/finance.costKurus` altındadır. `afterSales/{id}`
 * kök dokümanını ÜÇ ROL DE okuyabiliyor (`firestore.rules` §7) ve bu ekranda
 * rol kapısı yok — üretim personeli listeyi görüyor. Para kök dokümana
 * yazılsaydı atölye SSH maliyetini tarayıcıdan okurdu. Aynı hata sipariş
 * tarafında 1.569 kayıtta ölçülmüş ve maliyet `orders/{id}/private/finance`e
 * taşınarak kapatılmıştı. GİZLİLİK SINIRI = DOKÜMAN SINIRI.
 *
 * ── KOŞULLU ABONELİK ───────────────────────────────────────────────────────
 * `costView` yoksa sorgu HİÇ AÇILMAZ (`useDoc` `null` kaynak kabul eder).
 * Yetkisiz rolde açılan bir sorgu `permission-denied` üretir ve ekranda
 * gereksiz bir hata kutusu çıkarırdı — üstelik veri istemciye zaten gitmemeli.
 *
 * ── YAZMA CALLABLE ÜZERİNDEN ───────────────────────────────────────────────
 * Gider verisi PARA kaydıdır. `setSshCost` tutarın kuruş/tam sayı olduğunu ve
 * kök dokümandaki `costEntered` bayrağının tutarla tutarlı kaldığını garanti
 * eder; `firestore.rules` istemci yazmasını tamamen kapatır.
 * ═══════════════════════════════════════════════════════════════════════ */

interface SshFinanceDoc {
  /** SSH gider tutarı, KURUŞ. Alan YOKSA tutar girilmemiştir (0 ile aynı değil). */
  costKurus?: number;
  note?: string;
}

function SshCostEditor({ sshId }: { sshId: string }) {
  const can = useCan();
  const { data, loading, error } = useDoc<SshFinanceDoc>(
    // ★ Yetkisiz rolde sorgu hiç açılmaz.
    () => (can.seeCost ? doc(db, "afterSales", sshId, "private", "finance") : null),
    [sshId, can.seeCost],
  );

  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const current =
    data && typeof data.costKurus === "number" && Number.isFinite(data.costKurus)
      ? data.costKurus
      : null;

  useEffect(() => {
    setAmount(current === null ? "" : formatKurusInput(current));
    setNote(data?.note ?? "");
  }, [current, data?.note]);

  if (!can.seeCost) return null;

  async function save(clear: boolean) {
    setErr(null);

    let costKurus: number | null = null;
    if (!clear) {
      const parsed = parseKurus(amount);
      /**
       * ★ SIFIR GEÇERLİ, BOŞ DEĞİL. `!parsed` yazılsaydı garanti kapsamındaki
       * bedelsiz onarımın 0 TL'lik meşru kaydı reddedilirdi. `parseKurus`
       * bozuk girdide `null` döner; ikisi ayrı anlam taşır.
       */
      if (parsed === null || parsed < 0) {
        setErr("Tutarı sayı olarak yazın (örnek: 1.250,50). Bedelsiz onarım için 0 yazın.");
        return;
      }
      costKurus = parsed;
    }

    setBusy(true);
    try {
      await callable<
        { sshId: string; costKurus: number | null; note: string },
        { costEntered: boolean }
      >("setSshCost")({ sshId, costKurus, note });
      setEditing(false);
    } catch (e) {
      // ★ Sunucu mesajı KAYBEDİLMEZ; "bir hata oluştu"ya indirgenmez.
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-3 rounded-md border border-gray-300 bg-white p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-semibold text-gray-900">SSH gider tutarı</span>
        {!editing ? (
          <button
            type="button"
            className="text-sm font-medium text-blue-600 hover:text-blue-800"
            onClick={() => setEditing(true)}
          >
            {current === null ? "Tutar gir" : "Düzenle"}
          </button>
        ) : null}
      </div>

      {loading ? (
        <p className="mt-1 text-sm text-gray-500">Yükleniyor…</p>
      ) : error ? (
        <p role="alert" className="mt-1 text-sm text-red-700">
          {error}
        </p>
      ) : !editing ? (
        <p className="mt-1 text-sm">
          {current === null ? (
            /* ★ "0 TL" YAZILMAZ. Girilmemiş tutar sıfır değildir; finans
               ekranı bu kaydı SSH gideri toplamına katmaz ve sayısını yazar. */
            <span className="font-medium text-amber-800">
              Girilmemiş — bu kayıt SSH gideri toplamına dâhil edilmiyor
            </span>
          ) : (
            <span className="font-semibold tabular-nums text-gray-900">{tl(current)}</span>
          )}
          {data?.note ? <span className="ml-2 text-gray-500">· {data.note}</span> : null}
        </p>
      ) : (
        <div className="mt-2 space-y-3">
          <Field
            label="Tutar"
            hint="Bu SSH kaydının işletmeye maliyeti. Bedelsiz onarım için 0 yazın."
            htmlFor={`ssh-tutar-${sshId}`}
          >
            <MoneyInput id={`ssh-tutar-${sshId}`} value={amount} onChange={setAmount} />
          </Field>
          <Field label="Not" htmlFor={`ssh-tutar-not-${sshId}`}>
            <input
              id={`ssh-tutar-not-${sshId}`}
              type="text"
              className="input w-full"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Malzeme + işçilik, kargo dâhil…"
            />
          </Field>

          {err ? (
            <p role="alert" className="text-sm text-red-700">
              {err}
            </p>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-primary"
              disabled={busy}
              onClick={() => void save(false)}
            >
              {busy ? "Kaydediliyor…" : "Kaydet"}
            </button>
            <button
              type="button"
              className="btn-secondary"
              disabled={busy}
              onClick={() => setEditing(false)}
            >
              Vazgeç
            </button>
            {current !== null ? (
              <button
                type="button"
                className="text-sm text-red-700 hover:underline"
                disabled={busy}
                onClick={() => void save(true)}
              >
                Tutarı sil
              </button>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
